import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getBlogById, getAllBlogs } from "../api/blogs";

const SingleBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [blog, setBlog] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    getBlogById(id)
      .then((res) => {
        if (isMounted) setBlog(res.data);
      })
      .catch(() => {
        if (isMounted) setError("This blog post couldn't be found.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    getAllBlogs()
      .then((res) => {
        if (isMounted) setAllBlogs(res.data);
      })
      .catch(() => {
        /* related posts are a nice-to-have; fail silently */
      });

    window.scrollTo(0, 0);

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, scrolled)));
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const relatedPosts = useMemo(() => {
    if (!blog) return [];
    return allBlogs
      .filter((post) => post.id !== blog.id)
      .filter((post) => (post.tags || []).some((tag) => (blog.tags || []).includes(tag)))
      .slice(0, 3);
  }, [allBlogs, blog]);

  if (loading) {
    return (
      <section className="section">
        <div className="container">
          <p style={{ color: "var(--text-muted)" }}>Loading article…</p>
        </div>
      </section>
    );
  }

  if (error || !blog) {
    return (
      <section className="section" style={{ textAlign: "center" }}>
        <div className="container">
          <h1 style={{ color: "var(--text-primary)", marginBottom: "1rem" }}>Post not found</h1>
          <p style={{ color: "var(--text-muted)", marginBottom: "1.5rem" }}>
            {error || "We couldn't find the blog post you're looking for."}
          </p>
          <button className="btn btn-outline" onClick={() => navigate("/blogs")}>
            ← Back to Blog
          </button>
        </div>
      </section>
    );
  }

  const sections = blog.content || [];

  return (
    <>
      <div className="reading-progress" style={{ width: `${progress}%` }} />

      <section className="section">
        <div className="container">
          {/* Breadcrumb */}
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <span className="breadcrumb-sep">/</span>
            <Link to="/blogs">Blog</Link>
            <span className="breadcrumb-sep">/</span>
            <span style={{ color: "var(--text-primary)" }}>{blog.title}</span>
          </div>

          {/* Header */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
            {(blog.tags || []).map((tag) => (
              <span key={tag} className="tag">
                {tag}
              </span>
            ))}
          </div>
          <h1 style={{ color: "var(--text-primary)", fontSize: "2.25rem", marginBottom: "1rem", maxWidth: "48ch" }}>
            {blog.title}
          </h1>
          <div style={{ display: "flex", gap: "1rem", color: "var(--text-dim)", fontSize: "0.875rem", marginBottom: "2.5rem" }}>
            <span>{blog.author || "Kambham Srihari Reddy"}</span>
            <span>·</span>
            <span>{blog.date}</span>
            <span>·</span>
            <span>{blog.readTime}</span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: sections.length > 0 ? "1fr 240px" : "1fr",
              gap: "3rem",
            }}
          >
            {/* Article body */}
            <article className="prose">
              <p>{blog.summary}</p>
              {sections.map((section) => (
                <div key={section.id} id={section.id}>
                  <h2>{section.heading}</h2>
                  {(section.paragraphs || []).map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                  {section.list && (
                    <ul>
                      {section.list.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {/* Share */}
              <div className="divider" />
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="share-btn"
                  onClick={() => navigator.clipboard?.writeText(window.location.href)}
                >
                  Copy Link
                </button>
                <a
                  className="share-btn"
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Share on X
                </a>
                <a
                  className="share-btn"
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Share on LinkedIn
                </a>
              </div>
            </article>

            {/* Table of contents */}
            {sections.length > 0 && (
              <aside style={{ position: "sticky", top: "6rem", alignSelf: "start" }}>
                <p style={{ fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-dim)", marginBottom: "0.75rem" }}>
                  On This Page
                </p>
                {sections.map((section) => (
                  <a key={section.id} href={`#${section.id}`} className="toc-link">
                    {section.heading}
                  </a>
                ))}
              </aside>
            )}
          </div>

          {/* Related posts */}
          {relatedPosts.length > 0 && (
            <div style={{ marginTop: "4rem" }}>
              <h2 style={{ color: "var(--text-primary)", fontSize: "1.5rem", marginBottom: "1.5rem" }}>
                Related Articles
              </h2>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: "1.25rem",
                }}
              >
                {relatedPosts.map((post) => (
                  <Link key={post.id} to={`/blogs/${post.id}`} className="related-card">
                    <h3 style={{ color: "var(--text-primary)", fontSize: "1rem", marginBottom: "0.5rem" }}>
                      {post.title}
                    </h3>
                    <span style={{ color: "var(--text-dim)", fontSize: "0.8125rem" }}>{post.date}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default SingleBlog;
